// Typed client for Doctor AI Memory (Phase 2, /api/me/ai-memory) —
// docs/HMS_V4_AI_CONTEXT_BUILDING_ASSESSMENT.md §4.3/§4.4. Every call is
// scoped to the signed-in doctor server-side; there is no way to pass
// another user's id through this client.
import { api } from '@/lib/api/client';

export type MemoryCategory = 'format' | 'wording' | 'content' | 'language';
export type MemoryOrigin = 'explicit' | 'learned' | 'chat';
export type MemoryStatus = 'proposed' | 'snoozed' | 'active' | 'rejected' | 'archived';

export interface DoctorPreference {
  id: number;
  user_id: number;
  doc_type: string | null;
  category: MemoryCategory | 'prescribing';
  statement: string;
  origin: MemoryOrigin;
  status: MemoryStatus;
  version: number;
  /** Phase 3 (§4.4 point 1): how many of the doctor's last documents this learned pattern was found in — only meaningful for origin='learned'. */
  evidence_count?: number | null;
  evidence_refs?: number[] | null;
  snooze_until_count?: number | null;
  decided_at: string | null;
  created_at: string;
  updated_at: string;
}

export type ProposalDecision = 'approve' | 'not_now' | 'never' | 'undo';

export interface MemoryStatusResponse {
  preferences: DoctorPreference[];
  paused: boolean;
  enabled: boolean;
  doc_types: string[];
}

export function listMemory(): Promise<MemoryStatusResponse> {
  return api.get('/me/ai-memory');
}

export function createPreference(input: { docType?: string | null; category: MemoryCategory; statement: string; origin?: 'explicit' | 'chat' }): Promise<DoctorPreference> {
  return api.post('/me/ai-memory', { doc_type: input.docType ?? null, category: input.category, statement: input.statement, origin: input.origin ?? 'explicit' });
}

export function updatePreference(id: number, patch: { statement?: string; category?: MemoryCategory; docType?: string | null; active?: boolean }): Promise<DoctorPreference> {
  return api.patch(`/me/ai-memory/${id}`, { statement: patch.statement, category: patch.category, doc_type: patch.docType, active: patch.active });
}

export function deletePreference(id: number): Promise<void> {
  return api.delete(`/me/ai-memory/${id}`);
}

export function resetMemory(): Promise<{ removed: number }> {
  return api.post('/me/ai-memory/reset');
}

export function setMemoryPaused(paused: boolean): Promise<{ paused: boolean }> {
  return api.patch('/me/ai-memory/pause', { paused });
}

/** Phase 3 (§4.4 point 1): the single highest-evidence learned proposal for this doc_type, or null. */
export function getNextProposal(docType: string): Promise<{ proposal: DoctorPreference | null }> {
  return api.get('/me/ai-memory/proposals/next', { query: { doc_type: docType } });
}

/** Yes ("approve") / Not now ("not_now") / Never ("never") / Undo ("undo") on an inline proposal card. */
export function decideProposal(id: number, decision: ProposalDecision): Promise<DoctorPreference> {
  return api.patch(`/me/ai-memory/${id}/decision`, { decision });
}
